import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface AccentData {
  user_id: string;
  event: string;
  event_detail: string;
  event_detail_2: string;
  event_detail_3: string;
  event_timestamp: string;
  device_id: string;
  section_level: string;
  section: string;
  session_id: string;
  event_type: string;
  event_id: string;
}

interface GroupedData {
  [section: string]: {
    correct: number;
    incorrect: number;
  };
}

const Accent: React.FC = () => {
  const [accentData, setAccentData] = useState<AccentData[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from the given Accent endpoint
  const fetchAccentData = async () => {
    try {
      const response = await fetch(
        "https://ob38jihfoj.execute-api.us-east-1.amazonaws.com/dev/api/v1/metric-queries/20d7f83d-78ae-4522-a961-c8c2e51d697a"
      );
      const apiData = await response.json();
      // Transform the raw API data to our desired structure
      const transformed = transformAccentData(apiData);
      setAccentData(transformed);
    } catch (error) {
      console.error("Error fetching accent data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Transform the raw data from the API
  const transformAccentData = (apiData: any[]): AccentData[] => {
    // The data structure is [ [header...], [ [row], [row], ... ] ]
    if (!apiData || !apiData[1] || !apiData[1].length) return [];

    // Skip the top-level header row, then the internal header row, to get just the event rows
    return apiData[1].slice(1).map((row: string[]) => ({
      user_id: row[0],
      event: row[1],
      event_detail: row[2],
      event_detail_2: row[3],
      event_detail_3: row[4],
      event_timestamp: row[5],
      device_id: row[6],
      section_level: row[7],
      section: row[8],
      session_id: row[9],
      event_type: row[10],
      event_id: row[11],
    }));
  };

  // Helper function to process accent data for the stacked bar chart
  const processAccentData = () => {
    const groupedData: GroupedData = {};

    accentData.forEach((event) => {
      const section = event.section || "Unknown Section";
      if (!groupedData[section]) {
        groupedData[section] = { correct: 0, incorrect: 0 };
      }

      // We assume event_type can be "Accent_Correct" or "Accent_Incorrect"
      if (event.event_type === "Accent_Correct") {
        groupedData[section].correct += 1;
      } else if (event.event_type === "Accent_Incorrect") {
        groupedData[section].incorrect += 1;
      }
    });

    const labels = Object.keys(groupedData);
    return {
      labels,
      correctData: labels.map((section) => groupedData[section].correct),
      incorrectData: labels.map((section) => groupedData[section].incorrect),
      totalData: labels.map(
        (section) =>
          groupedData[section].correct + groupedData[section].incorrect
      ),
    };
  };

  useEffect(() => {
    fetchAccentData();
  }, []);

  if (loading) {
    return <div>Loading Accent data...</div>;
  }

  // Prepare the chart data
  const { labels, correctData, incorrectData, totalData } = processAccentData();

  const accentChartData = {
    labels,
    datasets: [
      {
        label: "Correct",
        data: correctData,
        backgroundColor: "#3366cc",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
      {
        label: "Incorrect",
        data: incorrectData,
        backgroundColor: "#dc3912",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
    ],
  };

  // Custom tooltip to show percentage
  const barOptions = {
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const datasetIndex = context.datasetIndex; // 0 -> Correct, 1 -> Incorrect
            const dataIndex = context.dataIndex; // index of the label
            const label = context.dataset.label; // "Correct" or "Incorrect"
            const value = context.parsed.y; // numeric value of this bar
            const total = totalData[dataIndex] || 0;

            let percentage = 0;
            if (total > 0) {
              percentage = (value / total) * 100;
            }
            const percentageStr = percentage.toFixed(2);

            return `${label}: ${value} of ${total} (${percentageStr}%)`;
          },
        },
      },
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true },
    },
  };

  return (
    <div>
      <h2>Accent</h2>
      <Bar data={accentChartData} options={barOptions} />
    </div>
  );
};

export default Accent;
